import { get, isEmpty } from 'lodash'
import { rem } from 'polished'
import React from 'react'
import styled from 'styled-components'
import { SummaryWrapper } from '@client/routes/Checkout/components/SummaryWrapper'
import { Button } from 'components/Button'
import { Flex, Grid } from 'components/Layout'
import { H4 } from 'components/Typography'
import { type CURRENCY_ISO_CODE } from 'constants/common'
import { type CartContextInterface, type CartContextItem } from 'providers/cart'
import { SPACE, mq } from 'Theme'
import {
  type CalculatePriceCheckoutQuery,
  type CartShipment,
} from 'types/graphql-generated'
import { useMedia } from 'useMedia'
import { hasCartAllItemsDiscountEligible } from 'utils/hasCartAllItemsDiscountEligible'
import { CollapsibleSummary } from './components/CollapsibleSummary'
import { ReturnPolicy } from './components/ReturnPolicy'

const GridPositionWrapper = styled.div`
  ${mq.from.TABLET`
    grid-row: 1/3;
    grid-column: 2/3;
  `}
`

const isItemWithReturnNotEligible = (cartItems: CartContextItem[]) =>
  cartItems.some(
    (item) => item.productVariant.managedCustomFields?.returnNotEligible
  )

const getPrice = ({
  nextStepCondition = false,
  loadingCondition = false,
  availableCondition = false,
  chooseShippingCondition = false,
}: {
  nextStepCondition?: boolean
  loadingCondition?: boolean
  availableCondition?: boolean
  chooseShippingCondition?: boolean
}) => {
  if (nextStepCondition) {
    return () => 'Calculated at next step'
  }
  if (loadingCondition) {
    return () => 'Loading...'
  }
  if (chooseShippingCondition) {
    return () => 'Choose a shipping method'
  }
  if (availableCondition) {
    return (price: number) => price
  }
  return () => 'We will reach out'
}

interface SummaryProps {
  cart: CartContextInterface['cart']
  isCheckout?: boolean
  total: number
  retailSubtotal?: number
  tradeSubtotal?: number
  currencyIsoCode: (typeof CURRENCY_ISO_CODE)[keyof typeof CURRENCY_ISO_CODE]
  pickedShippingMethods?: { [key: string]: string }
  groupedCartShipments?: { [key: string]: CartShipment[] }
  tax?: number
  shippingPrice?: number
  isOnShippingStep: boolean
  isPriceCalculationLoading?: boolean
  isCartShipmentsLoading?: boolean
  isPriceCalculationError?: boolean
  onProceedToCheckout?(...args: unknown[]): unknown
  discount?: number
  discountItems?: CalculatePriceCheckoutQuery['calculatePriceCheckout']['discountItems']
  couponDiscount?: number
  isTaxExemptionApplied?: boolean
}

export const Summary: React.FC<React.PWC<SummaryProps>> = ({
  cart,
  isCheckout = false,
  total,
  currencyIsoCode,
  pickedShippingMethods,
  groupedCartShipments,
  tax,
  shippingPrice,
  isOnShippingStep = false,
  isCartShipmentsLoading = false,
  isPriceCalculationLoading = false,
  isPriceCalculationError = false,
  onProceedToCheckout,
  discount: totalDiscount,
  discountItems,
  couponDiscount,
  isTaxExemptionApplied,
  retailSubtotal,
  tradeSubtotal,
}) => {
  const media = useMedia()

  const isMobile = media.MOBILE

  const cartItems = get(cart, 'items', null)
  const promoCode = get(cart, ['promoCode', 'code'], null)
  const linkToTerms = get(cart, ['promoCode', 'linkToTac'], null)

  const isShippingDataAvailable = !isEmpty(groupedCartShipments)

  const isPriceVisible =
    !isCartShipmentsLoading &&
    !isPriceCalculationLoading &&
    !isPriceCalculationError

  const isShippingPriceReadyToRender =
    isPriceVisible &&
    Boolean(Object.values(pickedShippingMethods ?? {}).filter(Boolean).length)

  const isLoading = isCartShipmentsLoading || isPriceCalculationLoading

  const areAllItemsDiscountEligible = hasCartAllItemsDiscountEligible({
    cartItems,
    discountItems,
    promoCode,
  })

  const isDiscountAvailable =
    totalDiscount > 0 ||
    (totalDiscount !== undefined && !areAllItemsDiscountEligible)

  return (
    <GridPositionWrapper>
      <SummaryWrapper width={{ MOBILE: 'auto', TABLET: rem(360) }}>
        <Grid gridGap={SPACE.PX_16}>
          <H4 mb={0}>Order summary</H4>
          <CollapsibleSummary
            cartItems={cartItems}
            discountItems={discountItems}
            prices={{
              shipping: getPrice({
                nextStepCondition: !isOnShippingStep,
                loadingCondition: isLoading,
                availableCondition: isShippingPriceReadyToRender,
                chooseShippingCondition:
                  isShippingDataAvailable && !isShippingPriceReadyToRender,
              })(shippingPrice),
              tax: getPrice({
                nextStepCondition: !isOnShippingStep,
                loadingCondition: isLoading,
                availableCondition: isPriceVisible,
              })(tax),
              totalDiscount: isDiscountAvailable
                ? {
                    total: totalDiscount,
                    areAllItemsDiscountEligible,
                    linkToTerms,
                  }
                : null,
              total: getPrice({
                loadingCondition: isLoading,
                availableCondition: isOnShippingStep
                  ? isPriceVisible && isShippingDataAvailable
                  : isPriceVisible,
              })(total),
              currencyIsoCode,
              couponDiscount,
              retailSubtotal: getPrice({
                loadingCondition: isLoading,
                availableCondition: isOnShippingStep
                  ? isPriceVisible && isShippingDataAvailable
                  : isPriceVisible,
              })(retailSubtotal),
              tradeSubtotal: getPrice({
                loadingCondition: isLoading,
                availableCondition: isOnShippingStep
                  ? isPriceVisible && isShippingDataAvailable
                  : isPriceVisible,
              })(tradeSubtotal),
            }}
            isExpandable={isCheckout}
            isOnShippingStep={isOnShippingStep}
            isTaxExemptionApplied={isTaxExemptionApplied}
          />
          {isCheckout ? (
            <ReturnPolicy
              isItemWithReturnNotEligible={isItemWithReturnNotEligible(
                cartItems
              )}
            />
          ) : (
            <Flex width="100%" justifyContent="center">
              <Button
                {...(isMobile ? { width: '75%' } : {})}
                p={SPACE.PX_15}
                onClick={onProceedToCheckout}
                aria-label="proceed to checkout"
              >
                Checkout
              </Button>
            </Flex>
          )}
        </Grid>
      </SummaryWrapper>
    </GridPositionWrapper>
  )
}
